import React, { useState } from 'react';
import {
  Tractor, Sprout, Sun, CloudRain, Snowflake, Flame, ShieldAlert,
  CheckCircle2, AlertTriangle, MapPin, Layers, Send, Smartphone,
  QrCode, ArrowRight, ArrowLeft, ExternalLink, RefreshCw, Info, CreditCard
} from 'lucide-react';

interface AgroDeepResearchHubViewProps {
  onBack: () => void;
  language?: 'uk' | 'en' | 'pl';
}

interface DistrictOption {
  id: string;
  name: string;
  oblast: string;
  lat: number;
  lon: number;
  soil: string;
}

const UKRAINIAN_DISTRICTS: DistrictOption[] = [
  { id: 'bila_tserkva', name: 'Білоцерківський район', oblast: 'Київська', lat: 49.79, lon: 30.11, soil: 'Типові чорноземи та темно-сірі опідзолені' },
  { id: 'boryspil', name: 'Бориспільський район', oblast: 'Київська', lat: 50.35, lon: 30.95, soil: 'Дерново-підзолисті та лучні ґрунти' },
  { id: 'uman', name: 'Уманський район', oblast: 'Черкаська', lat: 48.75, lon: 30.22, soil: 'Високородючі типові глибокі чорноземи' },
  { id: 'cherkasy', name: 'Черкаський район', oblast: 'Черкаська', lat: 49.44, lon: 32.06, soil: 'Піщані терасові та лучні чорноземи' },
  { id: 'vinnytsia', name: 'Вінницький район', oblast: 'Вінницька', lat: 49.23, lon: 28.47, soil: 'Опідзолені темно-сірі лісові' },
  { id: 'haisyn', name: 'Гайсинський район', oblast: 'Вінницька', lat: 48.81, lon: 29.38, soil: 'Глибокі малогумусні чорноземи' },
  { id: 'poltava', name: 'Полтавський район', oblast: 'Полтавська', lat: 49.59, lon: 34.55, soil: 'Типові та вилугувані чорноземи' },
  { id: 'kremenchuk', name: 'Кременчуцький район', oblast: 'Полтавська', lat: 49.07, lon: 33.42, soil: 'Звичайні чорноземи басейну Дніпра' },
  { id: 'dnipro', name: 'Дніпровський район', oblast: 'Дніпропетровська', lat: 48.46, lon: 35.04, soil: 'Звичайні середньогумусні чорноземи' },
  { id: 'synelnykove', name: 'Синельниківський район', oblast: 'Дніпропетровська', lat: 48.32, lon: 35.51, soil: 'Південні посушливі чорноземи' },
  { id: 'odesa', name: 'Одеський район', oblast: 'Одеська', lat: 46.48, lon: 30.73, soil: 'Південні чорноземи та каштанові ґрунти' },
  { id: 'podilsk', name: 'Подільський район', oblast: 'Одеська', lat: 47.74, lon: 29.53, soil: 'Звичайні глибокі чорноземи' },
  { id: 'kharkiv', name: 'Харківський район', oblast: 'Харківська', lat: 49.99, lon: 36.23, soil: 'Типові чорноземи лісостепової смуги' },
  { id: 'lviv', name: 'Львівський район', oblast: 'Львівська', lat: 49.84, lon: 24.03, soil: 'Сірі лісові та дерново-підзолисті' },
  { id: 'kropyvnytskyi', name: 'Кропивницький район', oblast: 'Кіровоградська', lat: 48.51, lon: 32.27, soil: 'Звичайні важкосуглинкові чорноземи' },
  { id: 'mykolaiv', name: 'Миколаївський район', oblast: 'Миколаївська', lat: 46.97, lon: 31.99, soil: 'Південні посухонестійкі чорноземи' }
];

export const AgroDeepResearchHubView: React.FC<AgroDeepResearchHubViewProps> = ({ onBack, language = 'uk' }) => {
  const [selectedDistrictId, setSelectedDistrictId] = useState<string>('bila_tserkva');
  const [customLat, setCustomLat] = useState<number>(49.79);
  const [customLon, setCustomLon] = useState<number>(30.11);
  const [areaHa, setAreaHa] = useState<number>(120);
  const [cropType, setCropType] = useState<string>('winter_wheat');
  const [isCustomCoord, setIsCustomCoord] = useState<boolean>(false);
  const [isAnalyzing, setIsAnalyzing] = useState<boolean>(false);
  const [lastUpdated, setLastUpdated] = useState<string>(new Date().toLocaleTimeString('uk-UA'));

  const activeDistrict = UKRAINIAN_DISTRICTS.find(d => d.id === selectedDistrictId) || UKRAINIAN_DISTRICTS[0];

  const handleDistrictChange = (distId: string) => {
    setSelectedDistrictId(distId);
    const dist = UKRAINIAN_DISTRICTS.find(d => d.id === distId);
    if (dist) {
      setCustomLat(dist.lat);
      setCustomLon(dist.lon);
    }
  };

  const handleRunAnalysis = () => {
    setIsAnalyzing(true);
    setTimeout(() => {
      setIsAnalyzing(false);
      setLastUpdated(new Date().toLocaleTimeString('uk-UA'));
    }, 600);
  };

  const botDeepLink = `https://t.me/Agrodeepresearchhub_bot?start=dist_${selectedDistrictId}`;

  return (
    <div className="space-y-8 animate-fade-in pb-16 font-mono text-slate-100 max-w-7xl mx-auto">
      {/* Top Header Navigation */}
      <div className="flex items-center justify-between gap-4 flex-wrap bg-slate-900 border border-slate-800 rounded-xl p-4">
        <button
          onClick={onBack}
          className="px-4 py-2 bg-slate-800 hover:bg-slate-700 text-slate-200 text-xs font-bold uppercase rounded-lg transition-colors flex items-center gap-2 border border-slate-700 cursor-pointer"
        >
          <ArrowLeft className="w-4 h-4" />
          <span>← Назад до Агро-Хабу</span>
        </button>

        <div className="flex items-center gap-3">
          <span className="px-3 py-1 text-[11px] font-extrabold uppercase bg-emerald-500/20 text-emerald-300 border border-emerald-400/40 rounded-lg flex items-center gap-1.5">
            <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
            24/7 Супутниковий Агро-Вартовий
          </span>
          <span className="px-3 py-1 text-[11px] font-extrabold uppercase bg-amber-500/20 text-amber-300 border border-amber-400/40 rounded-lg">
            Платформа «НІС-ЕКО ПРО 5»
          </span>
        </div>
      </div>

      {/* Hero Banner */}
      <div className="relative bg-gradient-to-r from-slate-950 via-emerald-950 to-slate-900 border border-emerald-500/40 rounded-3xl p-6 sm:p-10 shadow-2xl overflow-hidden">
        <div className="absolute right-0 top-0 w-96 h-96 bg-emerald-500/10 rounded-full blur-3xl pointer-events-none" />
        <div className="relative z-10 max-w-4xl space-y-4">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="px-3 py-1 text-[10px] font-extrabold uppercase bg-emerald-500/30 text-emerald-200 border border-emerald-400/50 rounded-md">
              🛰️ СУПУТНИКИ SENTINEL-2 & SENTINEL-1 + OPEN-METEO ECMWF
            </span>
            <span className="px-3 py-1 text-[10px] font-extrabold uppercase bg-blue-500/30 text-blue-200 border border-blue-400/50 rounded-md">
              100% ВЛАСНІСТЬ: БАНАХ АНДРІЙ СТЕПАНОВИЧ (Банах А.С.)
            </span>
          </div>

          <h1 className="text-2xl sm:text-4xl font-extrabold uppercase tracking-tight text-white leading-tight">
            🌾 AgroDeepResearchHub: Мультиризиковий Агромонітор
          </h1>
          <p className="text-xs sm:text-sm text-slate-300 leading-relaxed font-mono">
            Автономне виявлення критичних агрозагроз за координатами вашого поля: посуха, дефіцит вологи (NDMI), екстремальна спека (&gt;32°C), нічні заморозки (≤0°C на ґрунті), стрімке падіння вегетації (NDVI) та зливи зі шквалами. Миттєві Push-сповіщення у Telegram при оновленні космічних даних.
          </p>

          <div className="pt-2 flex items-center gap-4 flex-wrap">
            <a
              href={botDeepLink}
              target="_blank"
              rel="noreferrer"
              className="px-6 py-3.5 bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-extrabold text-xs uppercase rounded-xl transition-all shadow-lg flex items-center gap-2 cursor-pointer"
            >
              <Smartphone className="w-4 h-4" />
              <span>Підключити Telegram-бота (@Agrodeepresearchhub_bot)</span>
              <ExternalLink className="w-3.5 h-3.5" />
            </a>
            <span className="text-xs text-emerald-300 font-bold bg-emerald-950/60 px-3 py-2 rounded-lg border border-emerald-500/30">
              🎁 14 днів Free Trial для кожного поля
            </span>
          </div>
        </div>
      </div>

      {/* Control Panel: Location & Parameters */}
      <div className="bg-slate-900/90 border border-slate-800 rounded-2xl p-6 shadow-xl space-y-6">
        <div className="flex items-center justify-between border-b border-slate-800 pb-4 flex-wrap gap-4">
          <div className="flex items-center gap-2">
            <MapPin className="w-5 h-5 text-emerald-400" />
            <h2 className="text-lg font-bold uppercase text-white">Параметри моніторингу поля / району</h2>
          </div>

          <div className="flex items-center gap-2 flex-wrap">
            <button
              onClick={() => setIsCustomCoord(false)}
              className={`px-3 py-1.5 text-xs font-bold rounded-lg transition-colors cursor-pointer ${
                !isCustomCoord ? 'bg-emerald-600 text-white' : 'bg-slate-800 text-slate-400 hover:text-white'
              }`}
            >
              🗺️ Обрати Район України
            </button>
            <button
              onClick={() => setIsCustomCoord(true)}
              className={`px-3 py-1.5 text-xs font-bold rounded-lg transition-colors cursor-pointer ${
                isCustomCoord ? 'bg-emerald-600 text-white' : 'bg-slate-800 text-slate-400 hover:text-white'
              }`}
            >
              📍 Ввести точні координати (GPS)
            </button>
            <a
              href="https://t.me/Agrodeepresearchhub_bot"
              target="_blank"
              rel="noreferrer"
              className="px-3 py-1.5 text-xs font-bold rounded-lg bg-blue-600/30 text-blue-300 border border-blue-500/40 hover:bg-blue-600 hover:text-white transition-colors cursor-pointer flex items-center gap-1"
            >
              📁 Завантажити KML у Telegram
            </a>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {!isCustomCoord ? (
            <div className="md:col-span-2 space-y-2">
              <label className="text-xs text-slate-400 uppercase font-bold">Агрорайон України:</label>
              <select
                value={selectedDistrictId}
                onChange={(e) => handleDistrictChange(e.target.value)}
                className="w-full bg-slate-950 border border-slate-700 rounded-xl px-4 py-3 text-white text-xs font-mono focus:border-emerald-500 outline-none"
              >
                {UKRAINIAN_DISTRICTS.map((d) => (
                  <option key={d.id} value={d.id}>
                    {d.oblast} обл. — {d.name} ({d.lat.toFixed(2)}°N, {d.lon.toFixed(2)}°E)
                  </option>
                ))}
              </select>
              <p className="text-[11px] text-emerald-400/80">Типові ґрунти: {activeDistrict.soil}</p>
            </div>
          ) : (
            <>
              <div className="space-y-2">
                <label className="text-xs text-slate-400 uppercase font-bold">Широта (Lat):</label>
                <input
                  type="number"
                  step="0.0001"
                  value={customLat}
                  onChange={(e) => setCustomLat(parseFloat(e.target.value) || 0)}
                  className="w-full bg-slate-950 border border-slate-700 rounded-xl px-4 py-3 text-white text-xs font-mono focus:border-emerald-500 outline-none"
                />
              </div>
              <div className="space-y-2">
                <label className="text-xs text-slate-400 uppercase font-bold">Довгота (Lon):</label>
                <input
                  type="number"
                  step="0.0001"
                  value={customLon}
                  onChange={(e) => setCustomLon(parseFloat(e.target.value) || 0)}
                  className="w-full bg-slate-950 border border-slate-700 rounded-xl px-4 py-3 text-white text-xs font-mono focus:border-emerald-500 outline-none"
                />
              </div>
            </>
          )}

          <div className="space-y-2">
            <label className="text-xs text-slate-400 uppercase font-bold">Сільгоспкультура:</label>
            <select
              value={cropType}
              onChange={(e) => setCropType(e.target.value)}
              className="w-full bg-slate-950 border border-slate-700 rounded-xl px-4 py-3 text-white text-xs font-mono focus:border-emerald-500 outline-none"
            >
              <option value="winter_wheat">🌾 Озима пшениця</option>
              <option value="winter_rapeseed">🌿 Озимий ріпак</option>
              <option value="corn">🌽 Кукурудза на зерно</option>
              <option value="sunflower">🌻 Соняшник</option>
              <option value="soybean">🌱 Соя</option>
              <option value="orchard">🍎 Сади та ягідники</option>
            </select>
          </div>

          <div className="space-y-2">
            <label className="text-xs text-slate-400 uppercase font-bold">Площа ділянки (га):</label>
            <input
              type="number"
              value={areaHa}
              onChange={(e) => setAreaHa(parseFloat(e.target.value) || 1)}
              className="w-full bg-slate-950 border border-slate-700 rounded-xl px-4 py-3 text-white text-xs font-mono focus:border-emerald-500 outline-none"
            />
          </div>
        </div>

        <div className="flex items-center justify-between pt-2 flex-wrap gap-4 border-t border-slate-800/80">
          <div className="text-xs text-slate-400 flex items-center gap-2">
            <RefreshCw className={`w-3.5 h-3.5 text-emerald-400 ${isAnalyzing ? 'animate-spin' : ''}`} />
            <span>Останній аудит космічних даних: <b>{lastUpdated}</b></span>
          </div>

          <button
            onClick={handleRunAnalysis}
            disabled={isAnalyzing}
            className="px-6 py-2.5 bg-emerald-600 hover:bg-emerald-500 text-white font-extrabold text-xs uppercase rounded-xl transition-all shadow-md flex items-center gap-2 cursor-pointer disabled:opacity-50"
          >
            <RefreshCw className={`w-4 h-4 ${isAnalyzing ? 'animate-spin' : ''}`} />
            <span>{isAnalyzing ? 'Опитування супутників...' : 'Оновити діагностику поля'}</span>
          </button>
        </div>
      </div>

      {/* 5 Hazard Status Cards */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
        {/* 1. Посуха */}
        <div className="bg-slate-950 border border-amber-500/40 rounded-2xl p-5 space-y-3 relative overflow-hidden shadow-lg">
          <div className="flex items-center justify-between">
            <span className="text-[10px] font-extrabold uppercase bg-amber-500/20 text-amber-300 border border-amber-400/30 px-2 py-0.5 rounded">
              ПОСУХА & ВОЛОГА
            </span>
            <AlertTriangle className="w-4 h-4 text-amber-400" />
          </div>
          <div className="space-y-1">
            <h3 className="text-sm font-bold text-white uppercase">Дефіцит вологи</h3>
            <p className="text-2xl font-black text-amber-400">SM: 35.3%</p>
            <p className="text-[11px] text-slate-400">Посівний шар (0-7см): 33.0%</p>
            <p className="text-[11px] text-slate-400">Індекс NDMI: +0.002</p>
          </div>
          <div className="text-[10px] text-amber-300 font-bold bg-amber-950/60 p-2 rounded-lg border border-amber-500/20">
            ⚠️ Помірний дефіцит вологи кореневого шару.
          </div>
        </div>

        {/* 2. Спека */}
        <div className="bg-slate-950 border border-emerald-500/30 rounded-2xl p-5 space-y-3 relative overflow-hidden shadow-lg">
          <div className="flex items-center justify-between">
            <span className="text-[10px] font-extrabold uppercase bg-emerald-500/20 text-emerald-300 border border-emerald-400/30 px-2 py-0.5 rounded">
              СПЕКА (&gt;32°C)
            </span>
            <Flame className="w-4 h-4 text-emerald-400" />
          </div>
          <div className="space-y-1">
            <h3 className="text-sm font-bold text-white uppercase">Тепловий режим</h3>
            <p className="text-2xl font-black text-emerald-400">+19.0°C</p>
            <p className="text-[11px] text-slate-400">Поточна t°: +9.6°C</p>
            <p className="text-[11px] text-slate-400">Макс. 72 год: +19.0°C</p>
          </div>
          <div className="text-[10px] text-emerald-300 font-bold bg-emerald-950/60 p-2 rounded-lg border border-emerald-500/20">
            🟢 Сприятливий режим. Ризику теплового шоку немає.
          </div>
        </div>

        {/* 3. Заморозки */}
        <div className="bg-slate-950 border border-emerald-500/30 rounded-2xl p-5 space-y-3 relative overflow-hidden shadow-lg">
          <div className="flex items-center justify-between">
            <span className="text-[10px] font-extrabold uppercase bg-emerald-500/20 text-emerald-300 border border-emerald-400/30 px-2 py-0.5 rounded">
              ЗАМОРОЗКИ (≤0°C)
            </span>
            <Snowflake className="w-4 h-4 text-emerald-400" />
          </div>
          <div className="space-y-1">
            <h3 className="text-sm font-bold text-white uppercase">Темп. ґрунту</h3>
            <p className="text-2xl font-black text-emerald-400">+4.5°C</p>
            <p className="text-[11px] text-slate-400">Мін. прогноз: +4.5°C</p>
            <p className="text-[11px] text-slate-400">Ґрунт (0-7см): +12.3°C</p>
          </div>
          <div className="text-[10px] text-emerald-300 font-bold bg-emerald-950/60 p-2 rounded-lg border border-emerald-500/20">
            🟢 Приморозки відсутні. Сходи в безпеці.
          </div>
        </div>

        {/* 4. Біомаса NDVI */}
        <div className="bg-slate-950 border border-emerald-500/30 rounded-2xl p-5 space-y-3 relative overflow-hidden shadow-lg">
          <div className="flex items-center justify-between">
            <span className="text-[10px] font-extrabold uppercase bg-emerald-500/20 text-emerald-300 border border-emerald-400/30 px-2 py-0.5 rounded">
              ВЕГЕТАЦІЯ NDVI
            </span>
            <Sprout className="w-4 h-4 text-emerald-400" />
          </div>
          <div className="space-y-1">
            <h3 className="text-sm font-bold text-white uppercase">Індекс Sentinel-2</h3>
            <p className="text-2xl font-black text-emerald-400">0.430</p>
            <p className="text-[11px] text-slate-400">Оптимум сезону: 0.40 - 0.50</p>
            <p className="text-[11px] text-slate-400">Біомаса: Стабільна</p>
          </div>
          <div className="text-[10px] text-emerald-300 font-bold bg-emerald-950/60 p-2 rounded-lg border border-emerald-500/20">
            🟢 Фотосинтетична активність у нормі.
          </div>
        </div>

        {/* 5. Шторми та зливи */}
        <div className="bg-slate-950 border border-emerald-500/30 rounded-2xl p-5 space-y-3 relative overflow-hidden shadow-lg">
          <div className="flex items-center justify-between">
            <span className="text-[10px] font-extrabold uppercase bg-emerald-500/20 text-emerald-300 border border-emerald-400/30 px-2 py-0.5 rounded">
              ШТОРМИ & ОПАДИ
            </span>
            <CloudRain className="w-4 h-4 text-emerald-400" />
          </div>
          <div className="space-y-1">
            <h3 className="text-sm font-bold text-white uppercase">Опади 72 год</h3>
            <p className="text-2xl font-black text-emerald-400">0.0 мм</p>
            <p className="text-[11px] text-slate-400">Пориви вітру: 4.7 м/с</p>
            <p className="text-[11px] text-slate-400">Ризик граду: Низький</p>
          </div>
          <div className="text-[10px] text-emerald-300 font-bold bg-emerald-950/60 p-2 rounded-lg border border-emerald-500/20">
            🟢 Спокійна синоптична ситуація.
          </div>
        </div>
      </div>

      {/* High-Resolution Satellite Cartogram Section */}
      <div className="bg-slate-900 border border-slate-800 rounded-3xl p-6 sm:p-8 space-y-6 shadow-xl">
        <div className="flex items-center justify-between flex-wrap gap-4 border-b border-slate-800 pb-4">
          <div className="space-y-1">
            <div className="flex items-center gap-2">
              <span className="px-2.5 py-0.5 text-[9px] font-extrabold uppercase bg-emerald-500/20 text-emerald-300 border border-emerald-400/30 rounded">
                СУПУТНИКОВА ГРАФІКА 300 DPI
              </span>
              <span className="text-xs text-slate-400 font-bold">Sentinel-2 MSI (10 метрів)</span>
            </div>
            <h2 className="text-lg font-black uppercase text-white tracking-wide">
              Спектральні картограми: {activeDistrict.name} ({areaHa} га)
            </h2>
          </div>

          <a
            href={botDeepLink}
            target="_blank"
            rel="noreferrer"
            className="px-4 py-2 bg-emerald-600 hover:bg-emerald-500 text-white font-bold text-xs uppercase rounded-xl transition-all flex items-center gap-2 cursor-pointer shadow"
          >
            <span>Отримати HD-файл у Telegram</span>
            <ArrowRight className="w-3.5 h-3.5" />
          </a>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* NDVI Preview Card */}
          <div className="bg-slate-950 border border-emerald-500/30 rounded-2xl p-5 space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-xs font-bold text-white uppercase flex items-center gap-2">
                <Sprout className="w-4 h-4 text-emerald-400" />
                Карта біомаси посівів (NDVI)
              </span>
              <span className="text-xs font-mono font-bold text-emerald-400">Індекс: 0.430</span>
            </div>
            <div className="h-44 rounded-xl bg-gradient-to-tr from-amber-700 via-yellow-500 to-emerald-600 p-4 flex flex-col justify-between shadow-inner relative overflow-hidden">
              <div className="absolute inset-0 bg-slate-950/20 backdrop-blur-[1px]" />
              <div className="relative z-10 flex justify-between items-start">
                <span className="text-[10px] font-bold bg-slate-900/80 px-2 py-0.5 rounded text-white font-mono">
                  {customLat.toFixed(3)}°N, {customLon.toFixed(3)}°E
                </span>
                <span className="text-[10px] font-bold bg-emerald-950/80 px-2 py-0.5 rounded text-emerald-300 font-mono">
                  Густий травостій
                </span>
              </div>
              <div className="relative z-10 flex items-center justify-between">
                <div className="border border-red-500/80 border-dashed rounded-lg px-3 py-1 bg-red-950/40 text-[10px] font-bold text-white">
                  Контур KML: {areaHa} га
                </div>
                <span className="text-[10px] font-mono text-slate-200 bg-slate-900/80 px-2 py-0.5 rounded">
                  Роздільність: 10 м
                </span>
              </div>
            </div>
            <p className="text-[11px] text-slate-400 font-mono">
              Спектральний аналіз індексу відбиття червоного (B04) та ближнього інфрачервоного (B08) каналів.
            </p>
          </div>

          {/* NDMI Preview Card */}
          <div className="bg-slate-950 border border-blue-500/30 rounded-2xl p-5 space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-xs font-bold text-white uppercase flex items-center gap-2">
                <CloudRain className="w-4 h-4 text-blue-400" />
                Карта вологості листя та ґрунту (NDMI)
              </span>
              <span className="text-xs font-mono font-bold text-blue-400">Індекс: +0.002</span>
            </div>
            <div className="h-44 rounded-xl bg-gradient-to-tr from-rose-900 via-amber-600 to-cyan-500 p-4 flex flex-col justify-between shadow-inner relative overflow-hidden">
              <div className="absolute inset-0 bg-slate-950/20 backdrop-blur-[1px]" />
              <div className="relative z-10 flex justify-between items-start">
                <span className="text-[10px] font-bold bg-slate-900/80 px-2 py-0.5 rounded text-white font-mono">
                  Вологість 7-28 см: 35.3%
                </span>
                <span className="text-[10px] font-bold bg-blue-950/80 px-2 py-0.5 rounded text-blue-300 font-mono">
                  Помірний запас
                </span>
              </div>
              <div className="relative z-10 flex items-center justify-between">
                <div className="border border-red-500/80 border-dashed rounded-lg px-3 py-1 bg-red-950/40 text-[10px] font-bold text-white">
                  Кореневий шар: задовільно
                </div>
                <span className="text-[10px] font-mono text-slate-200 bg-slate-900/80 px-2 py-0.5 rounded">
                  SWIR канал (B11)
                </span>
              </div>
            </div>
            <p className="text-[11px] text-slate-400 font-mono">
              Фіксація поглинання води в короткохвильовому інфрачервоному діапазоні (1610 нм).
            </p>
          </div>
        </div>
      </div>

      {/* Telegram Bot Showcase & Deep Link Card */}
      <div className="bg-gradient-to-br from-slate-900 to-slate-950 border-2 border-emerald-500/50 rounded-3xl p-6 sm:p-8 shadow-2xl">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 items-center">
          <div className="lg:col-span-2 space-y-4">
            <div className="flex items-center gap-2">
              <span className="px-3 py-1 text-[10px] font-extrabold uppercase bg-emerald-500/20 text-emerald-300 border border-emerald-400/30 rounded-md">
                📱 ГОЛОВНИЙ МОБІЛЬНИЙ КАНАЛ
              </span>
              <span className="text-xs text-slate-400">@Agrodeepresearchhub_bot</span>
            </div>

            <h2 className="text-xl sm:text-2xl font-black uppercase text-white tracking-wide">
              Миттєві сповіщення просто у ваш смартфон
            </h2>
            <p className="text-xs sm:text-sm text-slate-300 leading-relaxed font-mono">
              Вам не потрібно щодня заходити на сайт і перевіряти супутникові карти. Наш робот під PM2 автоматично відстежує кожен новий проліт супутника Sentinel-2 та оновлення метеомоделей. Щойно температура перетне критичну межу або впаде вологість — ви отримаєте миттєвий Push-алерт прямо в Telegram.
            </p>

            <div className="space-y-2 pt-2">
              <div className="flex items-center gap-2 text-xs text-emerald-300">
                <CheckCircle2 className="w-4 h-4 text-emerald-400 shrink-0" />
                <span>Один клік на кнопку «📍 Надіслати GPS» у полі — і координати вже під моніторингом</span>
              </div>
              <div className="flex items-center gap-2 text-xs text-emerald-300">
                <CheckCircle2 className="w-4 h-4 text-emerald-400 shrink-0" />
                <span>Автоматичне попередження про нічний заморозок за 48 годин до настання мінусу</span>
              </div>
              <div className="flex items-center gap-2 text-xs text-emerald-300">
                <CheckCircle2 className="w-4 h-4 text-emerald-400 shrink-0" />
                <span>14 днів повного безкоштовного доступу без прив'язки картки</span>
              </div>
            </div>

            <div className="pt-4 flex items-center gap-4 flex-wrap">
              <a
                href={botDeepLink}
                target="_blank"
                rel="noreferrer"
                className="px-6 py-3.5 bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-extrabold text-xs uppercase rounded-xl transition-all shadow-lg flex items-center gap-2 cursor-pointer"
              >
                <Smartphone className="w-4 h-4" />
                <span>Запустити бота для {activeDistrict.name}</span>
                <ArrowRight className="w-4 h-4" />
              </a>
            </div>
          </div>

          <div className="bg-slate-950 border border-slate-800 rounded-2xl p-6 text-center space-y-3 flex flex-col items-center justify-center shadow-inner">
            <div className="w-36 h-36 bg-white p-3 rounded-2xl flex items-center justify-center shadow-lg">
              {/* QR-код стилізація */}
              <div className="w-full h-full bg-slate-900 rounded-xl flex flex-col items-center justify-center text-emerald-400">
                <QrCode className="w-16 h-16 text-emerald-400" />
                <span className="text-[8px] font-bold text-slate-200 mt-1 uppercase">QR для телефону</span>
              </div>
            </div>
            <p className="text-[11px] text-slate-400 font-mono">
              Наведіть камеру смартфона для миттєвого відкриття бота
            </p>
            <span className="text-[10px] text-emerald-400 font-bold bg-emerald-950/80 px-2.5 py-1 rounded border border-emerald-500/30">
              @Agrodeepresearchhub_bot
            </span>
          </div>
        </div>
      </div>

      {/* Subscription Pricing Tiers */}
      <div className="space-y-4">
        <div className="text-center space-y-2">
          <span className="px-3 py-1 text-[10px] font-extrabold uppercase bg-emerald-500/20 text-emerald-300 border border-emerald-400/30 rounded-md">
            ТАРИФНІ ПЛАНИ
          </span>
          <h2 className="text-xl sm:text-2xl font-black uppercase text-white">
            Зрозуміла комерційна вартість для кожного фермера
          </h2>
          <p className="text-xs text-slate-400 font-mono">
            Оплата в один дотик через Monobank Acquiring (Plata by mono) на рахунок ФОП Аксьонов С.М.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 pt-4">
          {/* Tier 1 */}
          <div className="bg-slate-950 border border-slate-800 hover:border-emerald-500/50 rounded-2xl p-6 flex flex-col justify-between gap-6 transition-all shadow-xl">
            <div className="space-y-3">
              <span className="text-[10px] font-extrabold uppercase bg-slate-800 text-slate-300 px-2.5 py-1 rounded">
                🥉 ФЕРМЕР БАЗОВИЙ
              </span>
              <div className="space-y-1">
                <p className="text-3xl font-black text-white">290 <span className="text-sm font-normal text-slate-400">грн / міс</span></p>
                <p className="text-xs text-slate-400">Для одноосібників та господарств до 300 га</p>
              </div>
              <ul className="space-y-2 text-xs text-slate-300 pt-3 border-t border-slate-800">
                <li className="flex items-center gap-2">✓ Моніторинг 1 району або поля</li>
                <li className="flex items-center gap-2">✓ Сповіщення про посуху та заморозки</li>
                <li className="flex items-center gap-2">✓ Ранковий щотижневий дайджест</li>
              </ul>
            </div>
            <a
              href={botDeepLink}
              target="_blank"
              rel="noreferrer"
              className="w-full py-3 bg-slate-800 hover:bg-slate-700 text-white font-extrabold text-xs uppercase rounded-xl transition-all text-center block cursor-pointer"
            >
              Спробувати 14 днів безкоштовно
            </a>
          </div>

          {/* Tier 2 */}
          <div className="bg-slate-950 border-2 border-emerald-500 rounded-2xl p-6 flex flex-col justify-between gap-6 transition-all shadow-2xl relative">
            <div className="absolute -top-3 right-6 bg-emerald-500 text-slate-950 text-[10px] font-black uppercase px-3 py-0.5 rounded-full shadow">
              ХІТ ПРОДАЖІВ
            </div>
            <div className="space-y-3">
              <span className="text-[10px] font-extrabold uppercase bg-emerald-500/20 text-emerald-300 px-2.5 py-1 rounded border border-emerald-500/30">
                🥈 АГРО-ПРОФІ
              </span>
              <div className="space-y-1">
                <p className="text-3xl font-black text-emerald-400">690 <span className="text-sm font-normal text-slate-400">грн / міс</span></p>
                <p className="text-xs text-slate-400">Для агрономів та господарств до 2 000 га</p>
              </div>
              <ul className="space-y-2 text-xs text-slate-300 pt-3 border-t border-slate-800">
                <li className="flex items-center gap-2">✓ До 5 полів з контурами та кадастрами</li>
                <li className="flex items-center gap-2">✓ Спектральні карти Sentinel-2 NDVI / NDMI 10м</li>
                <li className="flex items-center gap-2">✓ Миттєві Push-алерти за 48 годин</li>
                <li className="flex items-center gap-2">✓ Щотижневі PDF-звіти поля для агронома</li>
              </ul>
            </div>
            <a
              href={botDeepLink}
              target="_blank"
              rel="noreferrer"
              className="w-full py-3 bg-emerald-600 hover:bg-emerald-500 text-white font-extrabold text-xs uppercase rounded-xl transition-all text-center block shadow-lg cursor-pointer"
            >
              Підключити за 690 грн
            </a>
          </div>

          {/* Tier 3 */}
          <div className="bg-slate-950 border border-slate-800 hover:border-emerald-500/50 rounded-2xl p-6 flex flex-col justify-between gap-6 transition-all shadow-xl">
            <div className="space-y-3">
              <span className="text-[10px] font-extrabold uppercase bg-purple-500/20 text-purple-300 px-2.5 py-1 rounded border border-purple-500/30">
                🥇 АГРОХОЛДИНГ / ТРЕЙДЕР
              </span>
              <div className="space-y-1">
                <p className="text-3xl font-black text-white">1 990 <span className="text-sm font-normal text-slate-400">грн / міс</span></p>
                <p className="text-xs text-slate-400">Для корпоративних банків землі та елеваторів</p>
              </div>
              <ul className="space-y-2 text-xs text-slate-300 pt-3 border-t border-slate-800">
                <li className="flex items-center gap-2">✓ Необмежена кількість ділянок</li>
                <li className="flex items-center gap-2">✓ Розрахункові Акти екологічних збитків за ДСТУ</li>
                <li className="flex items-center gap-2">✓ Прямий API доступ для корпоративної 1С/ERP</li>
                <li className="flex items-center gap-2">✓ Персональний супровід агроексперта</li>
              </ul>
            </div>
            <a
              href={botDeepLink}
              target="_blank"
              rel="noreferrer"
              className="w-full py-3 bg-slate-800 hover:bg-slate-700 text-white font-extrabold text-xs uppercase rounded-xl transition-all text-center block cursor-pointer"
            >
              Підключити Корпоративний
            </a>
          </div>
        </div>
      </div>

      {/* Footer Legal & Ownership Passport */}
      <div className="bg-slate-950 border border-slate-800/80 rounded-2xl p-5 text-xs text-slate-400 flex items-center justify-between flex-wrap gap-4 font-mono">
        <div>
          <p className="text-white font-bold">Система супутникового мультиризикового агромоніторингу «AgroDeepResearchHub»</p>
          <p className="text-slate-400 text-[11px] mt-0.5">
            100% майнових та немайнових авторських прав належить: <b>Банах Андрій Степанович (Банах А.С.)</b> | РНОКПП: 2713213814 | ORCID: 0009-0000-2933-1143
          </p>
        </div>
        <div className="text-right text-[11px] text-emerald-400">
          Платформа «НІС-ЕКО ПРО 5» • Супутники Sentinel-1/2 • Open-Meteo ECMWF
        </div>
      </div>
    </div>
  );
};

export default AgroDeepResearchHubView;
